import React, { useMemo, useState, useCallback } from "react";
import { Select, Text, TextInput, Loader, Group, Stack, Paper } from "@mantine/core";
import {
  buildApprovedFundOptions,
  findApprovedFundOptionKey,
  mapApprovalFundToArProduct,
} from "../helpers/orderArFormFundUtils";
import { EMPTY_AR_FORM_PRODUCT } from "../helpers/arFormUtils";
import { useApprovedFundList } from "../contexts/approvedFundListContext";
import { attachSourceIndexes } from "../helpers/approvalFundListFundFields";
import {
  buildTableFilterChange,
  filterFunds,
} from "../helpers/approvalFundListFundsUtils";
import OrderArFormFundFilters from "./orderArFormFundFilters";

const OrderArFormApprovedFundSelect = ({
  form,
  label = "Fund Name 基金名稱",
}) => {
  const { funds, fundListName, loading, error } = useApprovedFundList();
  const [tableFilterQuery, setTableFilterQuery] = useState({});

  const indexedFunds = useMemo(() => attachSourceIndexes(funds), [funds]);
  const filteredFunds = useMemo(
    () => filterFunds(indexedFunds, { searchText: "", tableFilterQuery }),
    [indexedFunds, tableFilterQuery]
  );

  const selectedKey = findApprovedFundOptionKey(form.values, funds);

  const fundsForSelect = useMemo(() => {
    if (selectedKey == null || selectedKey === "") return filteredFunds;

    const selectedIndex = Number(selectedKey);
    if (Number.isNaN(selectedIndex)) return filteredFunds;

    const alreadyIncluded = filteredFunds.some(
      (fund) => fund.__sourceIndex === selectedIndex
    );
    if (alreadyIncluded) return filteredFunds;

    const selectedFund = indexedFunds.find(
      (fund) => fund.__sourceIndex === selectedIndex
    );
    return selectedFund ? [selectedFund, ...filteredFunds] : filteredFunds;
  }, [filteredFunds, indexedFunds, selectedKey]);

  const options = useMemo(
    () => buildApprovedFundOptions(fundsForSelect),
    [fundsForSelect]
  );

  const onTableFilterChange = useMemo(
    () =>
      buildTableFilterChange({
        setTableFilterQuery,
        setCurrentPage: () => {},
      }),
    []
  );

  const handleFilterChange = useCallback(
    (field, values, possibleValues) => {
      if (field == null) {
        setTableFilterQuery({});
        return;
      }
      onTableFilterChange(field, values, possibleValues);
    },
    [onTableFilterChange]
  );

  const applyFundSelection = (key) => {
    form.markUserEdited?.();
    if (key == null || key === "") {
      form.setValues({
        ...form.values,
        ...EMPTY_AR_FORM_PRODUCT(),
        _approvedFundKey: null,
      });
      return;
    }

    const fund = funds[Number(key)];
    form.setValues({
      ...form.values,
      ...mapApprovalFundToArProduct(fund),
      _approvedFundKey: key,
    });
  };

  if (loading) {
    return (
      <Group spacing="xs" mb="xs">
        <Loader size="xs" />
        <Text size="xs" color="dimmed">
          Loading approved fund list…
        </Text>
      </Group>
    );
  }

  if (!funds.length) {
    return (
      <div>
        <Text size="xs" color="orange" mb="xs">
          {error
            ? error
            : fundListName
            ? `${fundListName} has no funds. Enter fund details manually.`
            : "No effective approved fund list is available. Enter fund details manually."}
        </Text>
        <TextInput
          label={label}
          required
          {...form.getInputProps("fundName")}
        />
      </div>
    );
  }

  return (
    <Stack spacing="lg">
      {fundListName && (
        <Text size="sm" color="dimmed">
          {fundListName}
        </Text>
      )}

      <Paper withBorder p="md" radius="sm">
        <OrderArFormFundFilters
          funds={indexedFunds}
          tableFilterQuery={tableFilterQuery}
          onFilterChange={handleFilterChange}
          filteredCount={filteredFunds.length}
          totalCount={indexedFunds.length}
        />
      </Paper>

      <Select
        label={label}
        placeholder="Search and select a fund"
        required
        searchable
        clearable
        nothingFound="No matching fund"
        data={options}
        value={selectedKey}
        onChange={applyFundSelection}
        size="md"
      />
    </Stack>
  );
};

export default OrderArFormApprovedFundSelect;
